import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MapPin } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import PartnerBanner from '../../components/affiliate/PartnerBanner';
import NationalRelatedGuides from '../../components/national/NationalRelatedGuides';
import { useUserState, STATE_NAMES, isLiveState } from '../../lib/userState';

const SITE = 'https://buildpilot.com.au';
const CANONICAL = `${SITE}/how-to-build-a-home`;

// National, stage-by-stage overview. Links DOWN to the deep state guides
// rather than duplicating them.
const STEPS = [
  { n: 1, title: 'Set your budget and borrowing power', body: 'Work out your all-in number - land, construction, site costs and a contingency. Talk to a broker before you fall in love with a design.' },
  { n: 2, title: 'Choose land (or confirm your block)', body: 'Greenfield house-and-land or an established block for a knockdown rebuild. Check soil, slope, zoning and services before you commit.' },
  { n: 3, title: 'Pick the right type of builder', body: 'Volume, project, custom or architect. The right choice depends on your budget, block and how much you want to customise.' },
  { n: 4, title: 'Compare designs and get quotes', body: 'Compare inclusions, not just headline prices. Two quotes at the same price can differ by $50k once you read the fine print.' },
  { n: 5, title: 'Sign the building contract', body: 'Understand fixed-price vs cost-plus, the deposit, progress payments and your cooling-off rights. Have it reviewed before you sign.' },
  { n: 6, title: 'Council approval and engineering', body: 'Planning and building approvals, soil tests and engineering. Timeframes vary by council - build this into your plan.' },
  { n: 7, title: 'Construction', body: 'Slab, frame, lock-up, fit-off and handover. Expect 8-18 months depending on builder and complexity. Stay across each stage.' },
  { n: 8, title: 'Handover and moving in', body: 'Final inspection, defect list, keys - then the fun part: making the house a home.' },
];

const LOCAL = [
  { code: 'SA', city: 'Adelaide', to: '/home-builders-adelaide' },
  { code: 'VIC', city: 'Melbourne', to: '/home-builders-melbourne' },
];

const HowToBuildAHomePage = () => {
  const userState = useUserState();
  const orderedLocal = [...LOCAL].sort((a, b) => (a.code === userState ? -1 : b.code === userState ? 1 : 0));
  const showNonLiveNote = !!userState && !LOCAL.some((l) => l.code === userState) && !isLiveState(userState);
  const userStateName = STATE_NAMES[userState] || userState;
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${SITE}/guides` },
      { '@type': 'ListItem', position: 3, name: 'How to build a home', item: CANONICAL },
    ],
  };
  const howto = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to build a home in Australia',
    step: STEPS.map((s) => ({ '@type': 'HowToStep', position: s.n, name: s.title, text: s.body })),
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>How to Build a Home in Australia: Step-by-Step (2026) | BuildPilot</title>
        <meta name="description" content="The national step-by-step guide to building a home in Australia - budget, land, choosing a builder, contracts, approvals, construction and handover. Then find live builders in your state." />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content="How to Build a Home in Australia: Step-by-Step (2026)" />
        <meta property="og:description" content="Budget, land, builder, contract, approvals, construction, handover - the national build journey, then live builders for your state." />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(howto)}</script>
      </Helmet>

      <Header variant="light" />

      <main data-testid="national-how-to-page">
        <section className="border-b border-gray-100 bg-[#fafbfc]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe] mb-3">National guide · Australia</p>
            <h1 className="text-[32px] sm:text-[44px] font-bold text-[#1d1d1f] tracking-tight leading-[1.1] mb-4">
              How to build a home in Australia
            </h1>
            <p className="text-[16px] sm:text-[18px] text-[#6b7078] leading-[1.6] max-w-2xl">
              Building is a sequence, not a leap. Here are the eight stages every new-home journey runs through across Australia - what happens, and what to watch for. When you're ready for real builders and prices, we'll point you to your state.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <ol className="space-y-5">
            {STEPS.map((s) => (
              <li key={s.n} className="flex gap-4" data-testid={`build-step-${s.n}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full gradient-blue-bg text-white text-[15px] font-bold">
                  {s.n}
                </span>
                <div className="pt-0.5">
                  <h2 className="text-[18px] sm:text-[20px] font-bold text-[#1d1d1f] mb-1">{s.title}</h2>
                  <p className="text-[15px] text-[#6b7078] leading-[1.6]">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-4" data-testid="howto-superloop">
          <div className="rounded-2xl border border-gray-100 bg-[#fafbfc] p-5 sm:p-6">
            <h2 className="text-[18px] sm:text-[20px] font-bold text-[#1d1d1f] mb-2">Line up your internet early</h2>
            <p className="text-[14px] text-[#6b7078] leading-[1.6] mb-4">nbn connections at a brand-new build can take longer than you expect, so sort a fast, no lock-in plan before handover. Work through our <Link to="/new-home-setup-checklist" className="text-[#1d8efe] font-semibold hover:underline">new home setup checklist</Link> to tick off the rest.</p>
            <PartnerBanner size="leaderboard" supplier="superloop" topic="internet" stretch />
          </div>
        </section>

        <section className="bg-[#0a2540] text-white" data-testid="howto-local-funnel">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-[#34d17f]" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#34d17f]">Ready for real builders?</p>
            </div>
            <h2 className="text-[24px] sm:text-[32px] font-bold tracking-tight leading-[1.15] mb-3">
              Now line up builders where you're building.
            </h2>
            <p className="text-[15px] text-white/70 mb-7 max-w-2xl leading-[1.6]">
              Live builder matching is running in South Australia and Victoria and expanding across Australia. We'll match you with vetted builders suited to your project - free, no pressure.
            </p>
            {showNonLiveNote && (
              <div className="mb-6 rounded-2xl border border-[#34d17f]/25 bg-[#34d17f]/[0.08] px-5 py-4" data-testid="howto-non-live-note">
                <p className="text-[14px] text-white/90 leading-[1.6]">
                  <span className="font-semibold">Not live in {userStateName} yet</span> — it's on the rollout. This guide applies today; here's where live matching runs right now, plus a national match request.
                </p>
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              {orderedLocal.map((l) => (
                <Link key={l.city} to={l.to} className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-[14px] font-semibold transition-colors ${l.code === userState ? 'bg-[#34d17f] text-[#0a2540] hover:bg-[#34d17f]/90' : 'bg-white text-[#0a2540] hover:bg-white/90'}`} data-testid={`howto-local-${l.city.toLowerCase()}`}>
                  Compare {l.city} builders <ArrowRight className="w-4 h-4" />
                </Link>
              ))}
              <Link to="/find-a-builder" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-[14px] font-semibold hover:bg-white/10 transition-colors" data-testid="howto-find-builder">
                Find a builder <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-7">
              <Link to="/cost-to-build-a-house-australia" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#34d17f] hover:text-white transition-colors" data-testid="howto-to-cost-link">
                See what it costs to build in Australia <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <NationalRelatedGuides currentSlug="/how-to-build-a-home" />
      </main>

      <Footer />
    </div>
  );
};

export default HowToBuildAHomePage;
